import { useCallback, useEffect } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { LocationsClient } from "@/features/locations/LocationsClient";
import { listLocations, locationsKeys, type LocationsSearch } from "@/features/locations/locations.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/locations/")({
  // Reading is tms_locations (Relaties → Locaties); changing is tms_locations_write —
  // in the screen, and on the API regardless.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_locations") && !hasModule(context.session, "tms_locations_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): LocationsSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      q: str(search.q),
      country: str(search.country),
      requirement: str(search.requirement),
      hours: str(search.hours),
      isActive: str(search.isActive),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: LocationsScreen,
});

function LocationsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canManage = session.roles.includes("admin") || hasModule(session, "tms_locations_write");

  // The location card's back link returns to the list as it was left.
  useEffect(() => {
    rememberListSearch("/locations", search);
  }, [search]);

  const query = useQuery({
    queryKey: locationsKeys.list(search),
    queryFn: () => listLocations(search),
    placeholderData: keepPreviousData,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const openLocation = useCallback(
    (id: string) => {
      navigate({ to: "/locations/$id", params: { id } });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Locations">
      <LocationsClient
        items={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        updateParams={updateParams}
        onOpenLocation={openLocation}
        userId={session.userId}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        canManage={canManage}
      />
    </div>
  );
}
